/* mo-base-draw.js: the to-scale bed drawings for sections/mo-base.liquid (foundations and bed frames pages).
   Pure functions, no DOM: state + pieces in, SVG markup out. The page script (assets/mo-base.js) calls them on
   every change, and build/still.js calls the same code in node to write the server-rendered still, so the first
   paint and the live drawing are the same picture.
   Units are inches. Side view: 10 viewBox units per inch, floor at y 560. Top view: 6 units per inch.
   Product surfaces use the product's own colors (read from its store photo, passed in as data). Red is used only
   for dimension lines. */
(function (root) {
  'use strict';

  /* foundation sizes as Sealy and Stearns & Foster publish them, width x length; King is two 38 x 79 halves */
  var SIZES = { 'Twin': [38, 74], 'Twin XL': [38, 79], 'Full': [53, 74], 'Queen': [60, 79], 'King': [76, 79] };
  var S = 10, VBW = 1000, VBH = 600, VTOP = 90, FLOOR = 560, RIGHT = 870, RULER = 948;
  var P = 6, PW = 640, PH = 560;

  function n(v) { return Math.round(v * 10) / 10; }
  function esc(s) { return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;'); }
  function attrs(o) {
    var out = '';
    for (var k in o) if (o[k] !== undefined && o[k] !== null && o[k] !== '') out += ' ' + k + '="' + (typeof o[k] === 'number' ? n(o[k]) : esc(o[k])) + '"';
    return out;
  }
  function rect(x, y, w, h, extra) {
    var o = { x: x, y: y, width: Math.max(0, w), height: Math.max(0, h) };
    for (var k in extra) o[k] = extra[k];
    return '<rect' + attrs(o) + '/>';
  }
  function line(x1, y1, x2, y2, extra) {
    var o = { x1: x1, y1: y1, x2: x2, y2: y2 };
    for (var k in extra) o[k] = extra[k];
    return '<line' + attrs(o) + '/>';
  }
  function text(x, y, s, extra) {
    var o = { x: x, y: y };
    for (var k in extra) o[k] = extra[k];
    return '<text' + attrs(o) + '>' + esc(s) + '</text>';
  }
  function poly(pts, extra) {
    var o = { points: pts.map(function (p) { return n(p[0]) + ',' + n(p[1]); }).join(' ') };
    for (var k in extra) o[k] = extra[k];
    return '<polygon' + attrs(o) + '/>';
  }
  function inch(v) { return (Math.round(v * 4) / 4).toString().replace(/\.25$/, '¼').replace(/\.5$/, '½').replace(/\.75$/, '¾').replace(/^0(?=[¼½¾])/, ''); }

  /* The stack, bottom to top. Each layer: { key, y0 (inches above floor), h }. */
  function stack(st, pieces) {
    var out = [], y = 0;
    var fr = st.frame && pieces[st.frame];
    var fd = st.foundation && pieces[st.foundation];
    var o = st.h || {};
    if (fr) { var fh = o.frame != null ? o.frame : (fr.h != null ? fr.h : st.frameIn); out.push({ key: 'frame', y0: 0, h: fh, piece: fr }); y += fh; }
    if (fd) { var dh = o.foundation != null ? o.foundation : (fd.h != null ? fd.h : st.fdIn); out.push({ key: 'foundation', y0: y, h: dh, piece: fd }); y += dh; }
    var mh = o.mattress != null ? o.mattress : st.mattressIn;
    out.push({ key: 'mattress', y0: y, h: mh });
    y += mh;
    return { layers: out, total: y };
  }

  /* anim: optional { frame: {dy, sy}, foundation: {...}, mattress: {...} } in inches; dy lifts a piece, sy squashes it */
  function groupOpen(key, layer, anim) {
    var a = anim && anim[key];
    if (!a || (!a.dy && !a.sy)) return '<g class="mb-l mb-l--' + key + '">';
    var base = FLOOR - layer.y0 * S;
    var sy = 1 - (a.sy || 0);
    var t = 'translate(0 ' + n(-(a.dy || 0) * S + base * (1 - sy)) + ') scale(1 ' + sy.toFixed(4) + ')';
    return '<g class="mb-l mb-l--' + key + '" transform="' + t + '">';
  }

  /* Malouf rail frames: L-angle side rails (70 in. long, centered under the bed), tapered legs with a brass ring and a
     round glide (the store's GL SKUs are the glide version), thin square-tube center legs on the Universal. Legs on
     the near rail are drawn solid, legs further back in a lighter steel. */
  function railFrame(layer, x0, L, fr, measured) {
    var h = layer.h, top = FLOOR - h * S, out = '';
    var RL = Math.min(fr.railLen || L, L), rx0 = x0 + (L - RL) / 2 * S;
    var legs = fr.legs || [];
    function lx(u) { return rx0 + (1.4 + u * (RL - 2.8)) * S; }
    function leg(u, type, back) {
      var cx = lx(u), cls = back ? 'mb-steel mb-back' : 'mb-steel', s = '<g class="mb-leg">';
      if (type === 'tube') {
        s += rect(cx - 0.45 * S, top + 1.5 * S, 0.9 * S, h * S - 2.1 * S, { 'class': 'mb-steel mb-back' });
        s += rect(cx - 0.9 * S, FLOOR - 0.6 * S, 1.8 * S, 0.6 * S, { 'class': 'mb-glide mb-back', rx: 2 });
        return s + '</g>';
      }
      var yTop = top + 1.5 * S, yBot = FLOOR - 0.6 * S, span = yBot - yTop;
      var sock = Math.min(1.6 * S, span * 0.3), ring = yTop + sock;
      s += poly([[cx - 0.95 * S, yTop], [cx + 0.95 * S, yTop], [cx + 0.7 * S, ring], [cx - 0.7 * S, ring]], { 'class': cls });
      s += rect(cx - 0.72 * S, ring, 1.44 * S, Math.min(0.6 * S, span * 0.12), { 'class': 'mb-brass' + (back ? ' mb-back' : '') });
      var cone = ring + Math.min(0.6 * S, span * 0.12);
      s += poly([[cx - 0.62 * S, cone], [cx + 0.62 * S, cone], [cx + 0.3 * S, yBot], [cx - 0.3 * S, yBot]], { 'class': cls });
      s += rect(cx - 1.25 * S, yBot, 2.5 * S, 0.6 * S, { 'class': 'mb-glide' + (back ? ' mb-back' : ''), rx: 2.5 });
      return s + '</g>';
    }
    legs.forEach(function (lg) { if (lg[1] > 0.01 && lg[1] < 0.99) out += leg(lg[0], lg[2], true); });
    out += rect(rx0, top, RL * S, 1.5 * S, { 'class': 'mb-steel', rx: 1.5 });
    out += rect(rx0, top, RL * S, 0.32 * S, { 'class': 'mb-steel-hi' });
    for (var i = 1; i < RL / 5; i++) out += rect(rx0 + i * 5 * S - 4, top + 0.6 * S, 8, 0.4 * S, { 'class': 'mb-hole', rx: 2 });
    out += rect(rx0 - 0.1 * S, top - 3.2 * S, 0.5 * S, 4.7 * S, { 'class': 'mb-steel' });
    legs.forEach(function (lg) { if (lg[1] >= 0.99) out += leg(lg[0], lg[2], false); });
    if (measured) out += rect(rx0 - 6, top - 6, RL * S + 12, h * S + 6, { 'class': 'mb-measured', rx: 6 });
    return out;
  }

  function platformFrame(layer, x0, L, fr) {
    var h = layer.h, top = FLOOR - h * S, out = '', tube = 1.25 * S;
    var us = (fr.sideLegs || [0, 0.5, 1]);
    var xs = us.map(function (u) { return x0 + (2.2 + u * (L - 4.4)) * S; });
    var brace = FLOOR - (fr.stretcher || 4.2) * S;
    out += rect(xs[0], brace - 0.4 * S, xs[xs.length - 1] - xs[0], 0.8 * S, { 'class': 'mb-steel mb-back' });
    xs.forEach(function (x, i) {
      out += rect(x - 0.5 * S, top + tube - 2, 1.0 * S, FLOOR - (top + tube) + 2, { 'class': 'mb-steel' });
      var dir = i === 0 ? 1 : (i === xs.length - 1 ? -1 : 0);
      var ds = dir ? [dir] : [-1, 1];
      ds.forEach(function (d) {
        out += line(x + d * 0.4 * S, top + 5.2 * S, x + d * 4.6 * S, top + tube, { 'class': 'mb-brace' });
      });
    });
    out += rect(x0, top, L * S, tube, { 'class': 'mb-steel', rx: tube / 2 });
    out += rect(x0 + tube / 2, top + 1.5, L * S - tube, 0.28 * S, { 'class': 'mb-steel-hi', rx: 1 });
    return out;
  }

  function foundation(layer, x0, L, fd) {
    var top = FLOOR - (layer.y0 + layer.h) * S, H = layer.h * S, out = '';
    var c = fd.cover || {};
    out += rect(x0, top, L * S, H, { fill: c.side || '#e9e8e6', rx: 0.5 * S, 'class': 'mb-cover' });
    out += rect(x0, top, L * S, 0.75 * S, { fill: c.top || '#c9c9c4', rx: 0.4 * S });
    out += line(x0 + 4, top + 0.75 * S, x0 + L * S - 4, top + 0.75 * S, { stroke: c.seam || 'rgba(0,0,0,.35)', 'stroke-width': 1.4 });
    out += rect(x0 + 2, top + H - 0.32 * S, L * S - 4, 0.32 * S, { fill: c.base || 'rgba(0,0,0,.28)' });
    [0.18, 0.5, 0.82].forEach(function (u) {
      out += rect(x0 + u * L * S - 0.9 * S, top + H - 0.32 * S, 1.8 * S, 0.32 * S, { fill: c.seam || 'rgba(0,0,0,.35)' });
    });
    out += text(x0 + 2.2 * S, top + H / 2 + 6.5, (fd.short || 'Foundation') + '  ' + inch(layer.h) + ' in.' + (fd.h == null ? ' as set' : ''), { 'class': 'mb-tag', style: 'fill:' + (c.ink || '#1b1c1d') });
    if (fd.h == null) out += rect(x0 - 6, top - 6, L * S + 12, H + 12, { 'class': 'mb-measured', rx: 8 });
    return out;
  }

  function mattress(layer, x0, L) {
    var top = FLOOR - (layer.y0 + layer.h) * S, H = layer.h * S, out = '', tape = 0.55 * S;
    out += rect(x0 - 2, top, L * S + 4, H, { 'class': 'mb-mat', rx: 1.4 * S });
    out += rect(x0 - 2, top, L * S + 4, tape, { 'class': 'mb-tape', rx: 1.2 * S });
    out += rect(x0 - 2, top + H - tape, L * S + 4, tape, { 'class': 'mb-tape', rx: 1.2 * S });
    if (layer.h >= 13) out += line(x0 + 1.2 * S, top + 3 * S, x0 + L * S - 1.2 * S, top + 3 * S, { 'class': 'mb-stitch' });
    out += line(x0 + 1.2 * S, top + H * 0.62, x0 + L * S - 1.2 * S, top + H * 0.62, { 'class': 'mb-stitch mb-stitch--faint' });
    out += text(x0 + 2.2 * S, top + Math.min(H * 0.62 - 7, Math.max(H * 0.42 + 6, 3 * S + 19)), 'Mattress  ' + inch(layer.h) + ' in.', { 'class': 'mb-tag' });
    return out;
  }

  function ruler(maxIn) {
    var out = '<g class="mb-ruler" aria-hidden="true">';
    out += line(RULER, FLOOR, RULER, FLOOR - maxIn * S, { 'class': 'mb-rule' });
    for (var i = 0; i <= maxIn; i++) {
      var big = i % 6 === 0, y = FLOOR - i * S;
      out += line(RULER, y, RULER + (big ? 14 : (i % 3 === 0 ? 9 : 5)), y, { 'class': big ? 'mb-tick mb-tick--big' : 'mb-tick' });
      if (big && i > 0) out += text(RULER + 18, y + 5, String(i), { 'class': 'mb-rnum' });
    }
    out += text(RULER + 18, FLOOR + 5, '0', { 'class': 'mb-rnum' });
    out += text(RULER + 18, FLOOR - maxIn * S - 12, 'in.', { 'class': 'mb-rnum' });
    return out + '</g>';
  }

  function arrow(x, y, dir) {
    return poly([[x, y], [x - 5, y + dir * 10], [x + 5, y + dir * 10]], { 'class': 'mb-dimhead' });
  }

  /* st: { size, frame, foundation, mattressIn, frameIn }  pieces: { handle: piece }  anim: optional drop/squash */
  function elevation(st, pieces, anim) {
    var dims = SIZES[st.size] || SIZES.Queen, L = dims[1];
    var x0 = RIGHT - L * S;
    var sk = stack(st, pieces), out = '';
    out += '<defs><pattern id="mb-hatch" width="10" height="10" patternUnits="userSpaceOnUse" patternTransform="rotate(45)"><line x1="0" y1="0" x2="0" y2="10" class="mb-hatchline"/></pattern></defs>';
    out += rect(0, FLOOR, VBW, VBH - FLOOR, { fill: 'url(#mb-hatch)', 'class': 'mb-floorfill' });
    out += line(0, FLOOR, VBW, FLOOR, { 'class': 'mb-floor' });
    out += ruler(44);
    sk.layers.forEach(function (ly) {
      out += groupOpen(ly.key, ly, anim);
      if (ly.key === 'frame') out += ly.piece.style === 'platform' ? platformFrame(ly, x0, L, ly.piece) : railFrame(ly, x0, L, ly.piece, ly.piece.h == null);
      else if (ly.key === 'foundation') out += foundation(ly, x0, L, ly.piece);
      else out += mattress(ly, x0, L);
      out += '</g>';
    });
    /* frame label and clearance under a platform deck */
    var fl = sk.layers[0].key === 'frame' ? sk.layers[0] : null;
    if (fl) {
      var fr = fl.piece, lab = (fr.short || 'Frame') + '  ' + inch(fl.h) + ' in.' + (fr.h == null ? ' as set' : '');
      var ty = FLOOR + 26;
      if (fr.style === 'platform') ty = FLOOR - fl.h * S + Math.min(fl.h * S * 0.55, 5.6 * S) + 10;
      out += text(x0 + (fr.style === 'platform' ? 8.5 : 2) * S, ty, lab, { 'class': 'mb-tag mb-tag--frame' });
      if (fr.clear) {
        var cx = x0 + L * S * 0.5 + 9 * S, ct = FLOOR - fr.clear * S;
        out += line(cx, FLOOR - 2, cx, ct + 2, { 'class': 'mb-dim mb-dim--thin' });
        out += line(cx - 7, ct, cx + 7, ct, { 'class': 'mb-dim mb-dim--thin' });
        out += line(cx - 7, FLOOR, cx + 7, FLOOR, { 'class': 'mb-dim mb-dim--thin' });
        out += text(cx + 10, FLOOR - fr.clear * S * 0.5 + 5, inch(fr.clear) + ' in. clear', { 'class': 'mb-dimtext' });
      }
    } else {
      out += text(x0 + 2 * S, FLOOR + 26, 'On the floor, no frame', { 'class': 'mb-tag mb-tag--frame' });
    }
    /* total height: red dimension line with extension line from the mattress top */
    var topY = FLOOR - sk.total * S, dx = RIGHT + 34;
    out += '<g class="mb-total">';
    out += line(RIGHT + 4, topY, dx + 8, topY, { 'class': 'mb-ext' });
    out += line(dx, FLOOR - 1, dx, topY + 1, { 'class': 'mb-dim' });
    out += arrow(dx, topY + 1, 1) + arrow(dx, FLOOR - 1, -1);
    out += '</g>';
    return out;
  }

  /* Top view: length runs left to right (head at the left), width top to bottom. */
  function plan(st, pieces) {
    var dims = SIZES[st.size] || SIZES.Queen, W = dims[0], L = dims[1];
    var x0 = (PW - L * P) / 2 + 22, y0 = (PH - W * P) / 2 - 8, out = '';
    var fr = st.frame && pieces[st.frame], fd = st.foundation && pieces[st.foundation];
    function X(u) { return x0 + u * L * P; }
    function Y(v) { return y0 + v * W * P; }
    out += rect(x0, y0, L * P, W * P, { 'class': 'mb-pl-bed', rx: 6 });
    var fdParts = [];
    if (fd) {
      var c = fd.cover || {}, parts = st.size === 'King' ? 2 : 1;
      for (var q = 0; q < parts; q++) {
        var fy = y0 + q * (W * P / parts) + (q ? 3 : 0), fh = W * P / parts - (parts > 1 ? 3 : 0);
        fdParts.push([fy, fh]);
        out += rect(x0 + 1, fy + 1, L * P - 2, fh - 2, { fill: c.top || '#c9c9c4', 'fill-opacity': 0.5, rx: 5 });
      }
    }
    if (fr && fr.style === 'platform') {
      var halves = st.size === 'King' && fr.kingHalves ? 2 : 1;
      for (var hI = 0; hI < halves; hI++) {
        var py = y0 + hI * (W * P / halves), ph = W * P / halves;
        for (var s = 4; s < L - 2; s += 4.5) out += line(x0 + s * P, py + 6, x0 + s * P, py + ph - 6, { 'class': 'mb-pl-slat' });
        [0.33, 0.66].forEach(function (v) { out += line(x0 + 6, py + v * ph, x0 + L * P - 6, py + v * ph, { 'class': 'mb-pl-wire' }); });
        out += rect(x0 + 2, py + 2, L * P - 4, ph - 4, { 'class': 'mb-pl-tube', rx: 14 });
        (fr.sideLegs || [0, 0.5, 1]).forEach(function (u) {
          [0, 1].forEach(function (v) {
            out += '<circle' + attrs({ cx: x0 + (2.2 + u * (L - 4.4)) * P, cy: py + (v ? ph - 1.2 * P : 1.2 * P), r: 1.0 * P, 'class': 'mb-pl-leg' }) + '/>';
          });
        });
      }
    } else if (fr) {
      /* rail frame from above: two side rails 70 in. long, three telescoping cross arms (head, middle, foot), the
         lengthwise center support where Malouf fits one, and every leg */
      var RL = Math.min(fr.railLen || L, L), rx = x0 + (L - RL) / 2 * P, fw = Math.min(W, fr.maxW || W);
      var fy0 = Y(0.5) - fw * P / 2, center = W >= (fr.centerFrom || 0);
      function RX(u) { return rx + (1.4 + u * (RL - 2.8)) * P; }
      function RY(v) { return fy0 + (0.8 + v * (fw - 1.6)) * P; }
      out += rect(rx, fy0, RL * P, 1.6 * P, { 'class': 'mb-pl-rail' });
      out += rect(rx, fy0 + fw * P - 1.6 * P, RL * P, 1.6 * P, { 'class': 'mb-pl-rail' });
      [0, 0.5, 1].forEach(function (u) {
        var cx = RX(u);
        out += rect(cx - 0.8 * P, fy0, 1.6 * P, fw * P * 0.56, { 'class': 'mb-pl-rail' });
        out += rect(cx - 0.65 * P, fy0 + fw * P * 0.44, 1.3 * P, fw * P * 0.56, { 'class': 'mb-pl-rail mb-pl-rail--in' });
        for (var k = 0; k < 5; k++) out += rect(cx - 1.2, fy0 + fw * P * (0.4 + k * 0.045), 2.4, 4, { 'class': 'mb-pl-hole', rx: 1 });
      });
      if (center) out += rect(rx, Y(0.5) - 1.25 * P, RL * P, 2.5 * P, { 'class': 'mb-pl-rail mb-pl-center' });
      (fr.legs || []).forEach(function (lg) {
        var mid = lg[1] > 0.01 && lg[1] < 0.99;
        if (mid && !center) return;
        var cx = RX(lg[0]), cy = mid ? Y(0.5) : RY(lg[1]);
        if (lg[2] === 'tube') out += rect(cx - 0.6 * P, cy - 0.6 * P, 1.2 * P, 1.2 * P, { 'class': 'mb-pl-leg mb-pl-leg--c' });
        else out += '<circle' + attrs({ cx: cx, cy: cy, r: 1.25 * P, 'class': 'mb-pl-leg' + (mid ? ' mb-pl-leg--c' : '') }) + '/>';
      });
      if (W > fw) out += rect(x0, Y(0.5) - fw * P / 2, L * P, fw * P, { 'class': 'mb-pl-short' });
    } else {
      out += text(X(0.5), Y(0.5) + 5, 'No frame', { 'class': 'mb-pl-note', 'text-anchor': 'middle' });
    }
    fdParts.forEach(function (fp) {
      out += rect(x0 + 1, fp[0] + 1, L * P - 2, fp[1] - 2, { 'class': 'mb-pl-fd', fill: 'none', rx: 5 });
      if (fdParts.length > 1) out += text(X(0.5), fp[0] + fp[1] / 2 + 5, 'Twin XL half, 38 by ' + L + ' in.', { 'class': 'mb-pl-note mb-pl-note--chip', 'text-anchor': 'middle' });
    });
    out += text(x0 - 12, Y(0.5), 'head', { 'class': 'mb-pl-head', transform: 'rotate(-90 ' + n(x0 - 12) + ' ' + n(Y(0.5)) + ')', 'text-anchor': 'middle' });
    /* width and length dimensions */
    var lx = x0 - 30;
    out += line(lx, y0 + 1, lx, y0 + W * P - 1, { 'class': 'mb-dim mb-dim--thin' });
    out += line(lx - 6, y0, lx + 6, y0, { 'class': 'mb-dim mb-dim--thin' }) + line(lx - 6, y0 + W * P, lx + 6, y0 + W * P, { 'class': 'mb-dim mb-dim--thin' });
    out += text(lx - 10, Y(0.5), W + ' in.', { 'class': 'mb-dimtext', transform: 'rotate(-90 ' + n(lx - 10) + ' ' + n(Y(0.5)) + ')', 'text-anchor': 'middle' });
    var ly = y0 + W * P + 26;
    out += line(x0 + 1, ly, x0 + L * P - 1, ly, { 'class': 'mb-dim mb-dim--thin' });
    out += line(x0, ly - 6, x0, ly + 6, { 'class': 'mb-dim mb-dim--thin' }) + line(x0 + L * P, ly - 6, x0 + L * P, ly + 6, { 'class': 'mb-dim mb-dim--thin' });
    out += text(X(0.5), ly + 22, L + ' in.', { 'class': 'mb-dimtext', 'text-anchor': 'middle' });
    return out;
  }

  var api = { SIZES: SIZES, stack: stack, elevation: elevation, plan: plan, inch: inch, VIEW: '0 ' + VTOP + ' ' + VBW + ' ' + (VBH - VTOP), PLAN_VIEW: '0 0 ' + PW + ' ' + PH };
  root.MOBaseDraw = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
})(typeof window !== 'undefined' ? window : globalThis);
